import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';
import {suggest} from "../../../services/controlPanelApi";
import {toast} from "react-toastify";
import './UrgencyCheckbox.css'; // Importamos los estilos CSS
import {
    getProviderCategories,
    cleanSubcategoryName
} from '../../../utils/migrationHelpers'; // 🎯 Helpers de migración

const CategoriesStep = ({ formData, setFormData, categories, errors, isEditing = false }) => {
    const [selectedCategories, setSelectedCategories] = useState([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState({});
    const [newKeyword, setNewKeyword] = useState('');
    const [newCertificate, setNewCertificate] = useState('');
    const [urgencyChecks, setUrgencyChecks] = useState({});

    // Modales de sugerencia
    const [showCategoryModal, setShowCategoryModal] = useState(false);
    const [categorySuggestion, setCategorySuggestion] = useState('');
    const [showSubcategoryModal, setShowSubcategoryModal] = useState(false);
    const [subcategorySuggestion, setSubcategorySuggestion] = useState('');
    const [categoryForSuggestion, setCategoryForSuggestion] = useState('');

    // Constante para la subcategoría de urgencia
    const URGENCY_SUBCATEGORY = "DE URGENCIA";

    const categoryMap = useMemo(() => {
        const map = new Map();
        categories.forEach(c => {
            const subcats = c.subcategories || [];
            map.set(c.name, subcats);
        });
        return map;
    }, [categories]);

    // Mapa para obtener el ID de la categoría por su nombre
    const categoryIdMap = useMemo(() => {
        const map = new Map();
        categories.forEach(c => {
            const categoryId = c.id || c._id || c.name;
            map.set(c.name, categoryId);
        });
        return map;
    }, [categories]);



    // 🎯 Usamos cleanSubcategoryName del helper de migración

    // 🎯 Sincronizar el estado interno con formData.
    // Refleja el formData en cada cambio relevante para evitar carreras entre
    // - la llegada de formData (fetch del provider) y
    // - la llegada del catálogo de categorías disponibles (context).
    // Las categorías mostradas no dependen del catálogo (las pintamos siempre
    // que estén en formData). Las subcategorías sí dependen del catálogo para
    // resolver el prefijo de ID — mientras no esté cargado, sólo intentamos
    // matchear por nombre.
    useEffect(() => {
        const providerCategories = getProviderCategories(formData) || [];
        setSelectedCategories(providerCategories);

        if (providerCategories.length === 0) {
            setSelectedSubcategories({});
            setUrgencyChecks({});
            return;
        }

        const initialSubcategories = {};
        const initialUrgencyChecks = {};

        providerCategories.forEach((cat) => {
            const categoryId = categoryIdMap.get(cat) || cat;

            const subs = (formData.subcategories || []).filter((sub) => {
                const subStr = String(sub);
                return subStr.startsWith(`${cat}-`) || subStr.startsWith(`${categoryId}-`);
            });

            if (subs.length > 0) {
                initialSubcategories[cat] = subs;
                initialUrgencyChecks[cat] = subs.some(sub => cleanSubcategoryName(sub) === URGENCY_SUBCATEGORY);
            } else if (!isEditing) {
                const allSubs = categoryMap.get(cat) || [];
                initialSubcategories[cat] = allSubs.map(s => `${categoryId}-${s}`);
                const hasUrgencySub = allSubs.includes(URGENCY_SUBCATEGORY);
                if (hasUrgencySub) {
                    initialUrgencyChecks[cat] = true;
                }
            } else {
                initialSubcategories[cat] = [];
            }
        });

        setSelectedSubcategories(initialSubcategories);
        setUrgencyChecks(initialUrgencyChecks);
    }, [
        categories,
        isEditing,
        formData?.categoryNames,
        formData?.categories,
        formData?.subcategories,
        categoryIdMap,
        categoryMap,
    ]);

    // Función original para manejar selección de categorías y subcategorías
    const handleCategorySelection = (selectedCategories, selectedSubcategories) => {
        const updatedSubcategories = Object.values(selectedSubcategories).flat();

        // Verificar si realmente hay cambios antes de actualizar
        const currentCategories = getProviderCategories(formData) || [];
        const currentSubcategories = formData.subcategories || [];

        const categoriesChanged = JSON.stringify(currentCategories) !== JSON.stringify(selectedCategories);
        const subcategoriesChanged = JSON.stringify(currentSubcategories) !== JSON.stringify(updatedSubcategories);

        if (categoriesChanged || subcategoriesChanged) {

            setFormData(prevFormData => ({
                ...prevFormData,
                categories: selectedCategories,
                // Mantener categoryNames en sync para que el effect que deriva
                // selectedCategories desde formData (que prioriza categoryNames)
                // no revierta la selección del usuario en el siguiente render.
                categoryNames: selectedCategories,
                subcategories: updatedSubcategories,
            }));
        }
    };

    // Manejar cambio en categorías con auto-selección completa para categorías nuevas
    const handleCategoryChange = (selectedOptions) => {
        const safeOptions = selectedOptions || [];
        const selectedValues = safeOptions.map((opt) => opt.value);

        const prevSet = new Set(selectedCategories);
        const nextSet = new Set(selectedValues);

        const updatedSubcategories = { ...selectedSubcategories };
        const updatedUrgencyChecks = { ...urgencyChecks };

        // Para cada categoría seleccionada
        selectedValues.forEach((category) => {
            if (!updatedSubcategories[category]) updatedSubcategories[category] = [];
            // Si es una categoría nueva recién añadida, auto-seleccionar todas sus subcategorías
            if (!prevSet.has(category)) {
                const allSubs = categoryMap.get(category) || [];
                const categoryId = categoryIdMap.get(category) || category;
                updatedSubcategories[category] = allSubs.map(s => `${categoryId}-${s}`);

                // Verificar si existe la subcategoría de urgencia para esta categoría
                const hasUrgencySub = allSubs.includes(URGENCY_SUBCATEGORY);
                if (hasUrgencySub) {
                    updatedUrgencyChecks[category] = true;
                }
            }
        });

        // Eliminar subcategorías de categorías ya no seleccionadas
        Object.keys(updatedSubcategories).forEach((cat) => {
            if (!nextSet.has(cat)) {
                delete updatedSubcategories[cat];
                delete updatedUrgencyChecks[cat];
            }
        });

        setSelectedCategories(selectedValues);
        setSelectedSubcategories(updatedSubcategories);
        setUrgencyChecks(updatedUrgencyChecks);
        handleCategorySelection(selectedValues, updatedSubcategories);
    };

    // Manejar cambio en subcategorías (formato `${categoryId}-${sub}`)
    const handleSubcategoryChange = (category, selectedOptions) => {
        const currentSelectedOptions = selectedOptions || [];
        const categoryId = categoryIdMap.get(category) || category;
        const selectedSubcategoryValues = currentSelectedOptions.map((opt) => {
            return `${categoryId}-${opt.value}`;
        });

        // Verificar si la subcategoría DE URGENCIA está entre las seleccionadas
        const urgencySelected = currentSelectedOptions.some(opt => opt.value === URGENCY_SUBCATEGORY);

        // Si estaba marcada en el checkbox pero no está en la selección, la añadimos
        if (urgencyChecks[category] && !urgencySelected) {
            const allSubcats = categoryMap.get(category) || [];
            if (allSubcats.includes(URGENCY_SUBCATEGORY)) {
                selectedSubcategoryValues.push(`${categoryId}-${URGENCY_SUBCATEGORY}`);
            }
        }

        const updatedSubcategories = {
            ...selectedSubcategories,
            [category]: selectedSubcategoryValues,
        };

        setSelectedSubcategories(updatedSubcategories);
        handleCategorySelection(selectedCategories, updatedSubcategories);
    };

    // Nuevo manejador para el checkbox de urgencia
    const handleUrgencyCheckboxChange = (category, isChecked) => {
        const updatedUrgencyChecks = {
            ...urgencyChecks,
            [category]: isChecked
        };

        // Actualizar la lista de subcategorías seleccionadas
        const updatedSubcats = { ...selectedSubcategories };
        const currentSubcats = updatedSubcats[category] || [];

        // Filtrar la subcategoría de urgencia si existe
        const filteredSubcats = currentSubcats.filter(
            sub => cleanSubcategoryName(sub) !== URGENCY_SUBCATEGORY
        );

        // Si se marcó el checkbox, agregar la subcategoría de urgencia
        if (isChecked) {
            const categoryId = categoryIdMap.get(category) || category;
            filteredSubcats.push(`${categoryId}-${URGENCY_SUBCATEGORY}`);
        }

        updatedSubcats[category] = filteredSubcats;

        setUrgencyChecks(updatedUrgencyChecks);
        setSelectedSubcategories(updatedSubcats);
        handleCategorySelection(selectedCategories, updatedSubcats);
    };

    // Palabras clave como chips + input (similar a sucursales)
    const addKeyword = () => {
        const value = (newKeyword || '').trim();
        if (!value) return;
        const current = formData.keywords || [];
        const exists = current.some(k => (k || '').trim().toLowerCase() === value.toLowerCase());
        if (exists) { setNewKeyword(''); return; }
        setFormData((prev) => ({ ...prev, keywords: [...current, value] }));
        setNewKeyword('');
    };

    const removeKeyword = (index) => {
        const updated = (formData.keywords || []).filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, keywords: updated }));
    };

    // Matrículas como chips + input (solo servicios)
    const addCertificateChip = () => {
        const value = (newCertificate || '').trim();
        if (!value) return;

        // Validar que solo contenga números
        if (!/^\d+$/.test(value)) {
            toast.error('Solo se permiten números en el campo de matrículas');
            return;
        }

        const current = formData.certificates || [];
        const exists = current.some(c => (c || '').trim().toLowerCase() === value.toLowerCase());
        if (exists) { setNewCertificate(''); return; }
        setFormData((prev) => ({ ...prev, certificates: [...current, value] }));
        setNewCertificate('');
    };

    const removeCertificateChip = (index) => {
        const updated = (formData.certificates || []).filter((_, i) => i !== index);
        setFormData((prev) => ({ ...prev, certificates: updated }));
    };

    const categoryOptions = categories.map((cat) => ({
        value: cat.name,
        label: cat.name,
    }));

    const normalizedKeywords = (formData.keywords || []).filter(k => (k || '').trim());
    const normalizedCertificates = (formData.certificates || []).filter(c => (c || '').trim());

    // Sugerencias
    const openSubcategoryModal = (category) => {
        setCategoryForSuggestion(category);
        setSubcategorySuggestion('');
        setShowSubcategoryModal(true);
    };

    const addCategorySuggestion = async () => {
        const val = (categorySuggestion || '').trim();
        if (!val) return;

        // Validar que el nombre no contenga guiones
        if (val.includes('-')) {
            toast.error('El nombre de la categoría no puede contener guiones (-)');
            return;
        }

        // Si estamos en modo edición, enviar al servidor en lugar de agregar al formulario
        if (isEditing) {
            try {
                await suggest({
                    suggestion: `Nueva categoría sugerida: ${val}`,
                    type: 'category',
                    providerType: formData.type || 'unknown'
                });
                toast.success('Tu sugerencia ha sido enviada y será revisada por el equipo');
                setCategorySuggestion('');
                setShowCategoryModal(false);
            } catch (error) {
                console.error('Error al enviar sugerencia:', error);
                toast.error('No se pudo enviar la sugerencia. Inténtalo nuevamente.');
            }
            return;
        }

        // Comportamiento original para registro
        const newCategory = `${val} (sugerencia)`;
        const updatedCats = [...selectedCategories, newCategory];
        // Inicializar subcats vacías para la sugerida
        const updatedSubcats = { ...selectedSubcategories, [newCategory]: [] };
        setSelectedCategories(updatedCats);
        setSelectedSubcategories(updatedSubcats);
        handleCategorySelection(updatedCats, updatedSubcats);
        setCategorySuggestion('');
        setShowCategoryModal(false);
    };

    const addSubcategorySuggestion = async () => {
        const val = (subcategorySuggestion || '').trim();
        if (!val || !categoryForSuggestion) return;

        // Validar que el nombre no contenga guiones
        if (val.includes('-')) {
            toast.error('El nombre de la subcategoría no puede contener guiones (-)');
            return;
        }

        // Si estamos en modo edición, enviar al servidor en lugar de agregar al formulario
        if (isEditing) {
            try {
                await suggest({
                    suggestion: `Nueva subcategoría sugerida: "${val}" para la categoría "${categoryForSuggestion}"`,
                    userId: formData.username,
                    type: 'subcategory'
                });
                toast.success('Tu sugerencia ha sido enviada y será revisada por el equipo');
                setSubcategorySuggestion('');
                setCategoryForSuggestion('');
                setShowSubcategoryModal(false);
            } catch (error) {
                console.error('Error al enviar sugerencia:', error);
                toast.error('No se pudo enviar la sugerencia. Inténtalo nuevamente.');
            }
            return;
        }

        // Comportamiento original para registro
        // Mantener formato con etiqueta de sugerencia y prefijo de categoría ID
        const bareCategory = categoryForSuggestion.replace(' (sugerencia)', '');
        const categoryId = categoryIdMap.get(categoryForSuggestion) || categoryForSuggestion;
        const newSubId = `${categoryId}-${val} (sugerencia - ${bareCategory})`;
        const list = selectedSubcategories[categoryForSuggestion] || [];
        const updatedSubcats = {
            ...selectedSubcategories,
            [categoryForSuggestion]: [...list, newSubId]
        };
        setSelectedSubcategories(updatedSubcats);
        handleCategorySelection(selectedCategories, updatedSubcats);
        setSubcategorySuggestion('');
        setCategoryForSuggestion('');
        setShowSubcategoryModal(false);
    };

    // Estilos inline simples para modal (evita dependencias CSS externas)
    const modalStyles = {
        overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 16 },
        content: { background: 'var(--card-background-dark)', border: '1px solid var(--input-border-dark)', borderRadius: 12, width: '100%', maxWidth: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 24px rgba(0,0,0,.35)' },
        header: { padding: '16px 20px', borderBottom: '1px solid var(--input-border-dark)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
        body: { padding: 20 },
        footer: { padding: '0 20px 20px', display: 'flex', gap: 8, justifyContent: 'flex-end' }
    };

    // Estilos del portal del menú para asegurar overlay correcto
    const selectPortalStyles = {
        menuPortal: (base) => ({ ...base, zIndex: 9999 })
    };

    return (
        <div className="step-content-wrapper">
            <div className="step-header">
                <h2>Categorías y Especialidades</h2>
                <p>Define en qué te especializas para que los clientes te encuentren fácilmente</p>
            </div>

            <div className="form-section">
                {/* Categorías principales */}
                <div className="form-group">
                    <label>
                        Categorías <span className="required">*</span>
                    </label>
                    <div className="field-help">
                        Selecciona todas las categorías que apliquen a tu negocio
                    </div>

                    <Select
                        isMulti
                        classNamePrefix="react-select"
                        className="react-select-container-bordered"
                        options={categoryOptions}
                        value={selectedCategories.map((cat) => ({ value: cat, label: cat }))}
                        placeholder="Selecciona categorías"
                        onChange={handleCategoryChange}
                        noOptionsMessage={() => "No hay más opciones. Podés escribir una."}
                        // Props defensivas
                        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                        menuPosition="fixed"
                        closeMenuOnScroll={true}
                        styles={selectPortalStyles}
                    />
                    <div className="field-footer">
                        {errors.categories && <span className="error-message">{errors.categories}</span>}
                        <div className="provider-suggestion-link" style={{ marginTop: 6 }}>
                            <button
                                type="button"
                                onClick={() => setShowCategoryModal(true)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    color: 'var(--primary-color)',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                }}
                            >
                                Sugerir nueva categoría
                            </button>
                        </div>
                    </div>
                </div>

                {/* Subcategorías */}
                {selectedCategories.map((category) => {
                    // Para categorías sugeridas, permitir agregar subcategorías sugeridas
                    const isSuggestedCategory = category.includes(' (sugerencia)');
                    const subcategoryList = isSuggestedCategory ? [] : (categoryMap.get(category) || []);

                    // Filtrar la subcategoría DE URGENCIA para mostrarla como checkbox separado
                    const filteredSubcategoryList = subcategoryList.filter(sub => sub !== URGENCY_SUBCATEGORY);
                    const hasUrgencyOption = subcategoryList.includes(URGENCY_SUBCATEGORY);

                    // Filtrar también las subcategorías seleccionadas para excluir DE URGENCIA del select
                    const currentValues = (selectedSubcategories[category] || [])
                        .map((sub) => cleanSubcategoryName(sub))
                        .filter(sub => sub !== URGENCY_SUBCATEGORY);

                    return (
                        <div key={category} className="form-group subcategory-group">
                            <label>Subcategorías para {category}</label>
                            <div className="field-help">
                                Borrá las que no correspondan
                            </div>

                            {filteredSubcategoryList.length > 0 ? (
                                <Select
                                    isMulti
                                    classNamePrefix="react-select"
                                    className="react-select-container-bordered"
                                    options={filteredSubcategoryList.map((sub) => ({value: sub, label: sub}))}
                                    value={currentValues.map((val) => ({ value: val, label: val }))}
                                    placeholder={`Selecciona subcategorías de ${category}`}
                                    noOptionsMessage={() => "No hay más opciones."}
                                    onChange={(options) => handleSubcategoryChange(category, options)}
                                    // Props defensivas
                                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                                    menuPosition="fixed"
                                    closeMenuOnScroll={true}
                                    styles={selectPortalStyles}
                                />
                            ) : (
                                <div className="empty-state compact">
                                    {isSuggestedCategory
                                        ? "Esta es una categoría sugerida. Podés agregar subcategorías usando el botón de abajo."
                                        : "No hay subcategorías disponibles para esta categoría"
                                    }
                                </div>
                            )}

                            {/* Mostrar subcategorías sugeridas existentes para esta categoría */}
                            {isSuggestedCategory && currentValues.length > 0 && (
                                <div className="suggested-subcategories">
                                    <div className="field-help" style={{ marginTop: '8px' }}>
                                        Subcategorías sugeridas:
                                    </div>
                                    <div className="preview-tags" style={{ marginTop: '4px' }}>
                                        {currentValues.map((val, index) => (
                                            <span key={index} className="preview-tag outline">
                                                {val}
                                                <button
                                                    type="button"
                                                    className="remove-btn"
                                                    style={{ marginLeft: '6px' }}
                                                    onClick={() => {
                                                        const updated = currentValues.filter((_, i) => i !== index);
                                                        const categoryId = categoryIdMap.get(category) || category;
                                                        const updatedSubcats = {
                                                            ...selectedSubcategories,
                                                            [category]: updated.map(v => `${categoryId}-${v}`)
                                                        };
                                                        setSelectedSubcategories(updatedSubcats);
                                                        handleCategorySelection(selectedCategories, updatedSubcats);
                                                    }}
                                                >
                                                    ×
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Checkbox para servicio de urgencia con clases CSS */}
                            {hasUrgencyOption && (
                                <div className={`urgency-checkbox-container ${urgencyChecks[category] ? 'active' : ''}`}>
                                    <div className="urgency-label-container">
                                        <span className="urgency-icon">⚡</span>
                                        <span className="urgency-label">Servicio DE URGENCIA</span>
                                    </div>
                                    <label className="urgency-custom-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={urgencyChecks[category] || false}
                                            onChange={(e) => handleUrgencyCheckboxChange(category, e.target.checked)}
                                            id={`urgency-${category}`}
                                        />
                                        <span className="checkmark"></span>
                                    </label>
                                </div>
                            )}

                            {/* Opcional: Descripción para el servicio de urgencia */}
                            {hasUrgencyOption && urgencyChecks[category] && (
                                <div className="urgency-description">
                                    Los clientes pueden encontrarte cuando busquen servicios urgentes en esta categoría.
                                </div>
                            )}

                            <div className="field-footer">
                                <div className="provider-suggestion-link" style={{ marginTop: 6 }}>
                                    <button
                                        type="button"
                                        onClick={() => openSubcategoryModal(category)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            padding: 0,
                                            color: 'var(--primary-color)',
                                            textDecoration: 'underline',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        Sugerir nueva subcategoría
                                    </button>
                                </div>
                            </div>
                        </div>
                    );
                })}

                {/* Palabras clave como chips + input - COMENTADO TEMPORALMENTE */}
                {/*
                <div className="form-group">
                    <label>Palabras clave</label>
                    <div className="field-help">
                        Agrega términos que tus clientes podrían buscar para encontrarte
                    </div>

                    <div className="selected-items">
                        {normalizedKeywords.length > 0 ? (
                            normalizedKeywords.map((keyword, index) => (
                                <div key={`${keyword}-${index}`} className="selected-item">
                                    <span>{keyword}</span>
                                    <button
                                        type="button"
                                        className="remove-btn"
                                        onClick={() => removeKeyword(index)}
                                    >
                                        ×
                                    </button>
                                </div>
                            ))
                        ) : (
                            <div className="empty-state compact">No hay palabras clave</div>
                        )}
                    </div>

                    <div className="add-field-container">
                        <input
                            type="text"
                            className="form-input"
                            placeholder="Escribe una palabra clave y presiona Enter"
                            value={newKeyword}
                            onChange={(e) => setNewKeyword(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addKeyword(); } }}
                        />
                        <button
                            type="button"
                            className="btn btn-outline add-btn"
                            onClick={addKeyword}
                        >
                            Agregar
                        </button>
                    </div>
                </div>
                */}

                {/* Matrículas - SOLO SERVICIOS como chips + input */}
                {formData.type === 'service' && (
                    <div className="form-group">
                        <label>Matrículas <span className="required">*</span></label>
                        <div className="field-help">
                            Ingresa tus números de matrícula profesional, certificaciones o habilitaciones que poseas (solo números)
                        </div>

                        <div className="selected-items">
                            {normalizedCertificates.length > 0 ? (
                                normalizedCertificates.map((certificate, index) => (
                                    <div key={`${certificate}-${index}`} className="selected-item">
                                        <span>{certificate}</span>
                                        <button
                                            type="button"
                                            className="remove-btn"
                                            onClick={() => removeCertificateChip(index)}
                                        >
                                            ×
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="empty-state compact">No hay matrículas cargadas</div>
                            )}
                        </div>

                        <div className="add-field-container">
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Número de matrícula o certificación"
                                value={newCertificate}
                                onChange={(e) => setNewCertificate(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCertificateChip(); } }}
                            />
                            <button
                                type="button"
                                className="btn btn-outline add-btn"
                                onClick={addCertificateChip}
                            >
                                Agregar
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Modal sugerencia de categoría */}
            {showCategoryModal && (
                <div style={modalStyles.overlay}>
                    <div style={modalStyles.content}>
                        <div style={modalStyles.header}>
                            <h3 style={{ margin: 0 }}>Sugerir nueva categoría</h3>
                            <button className="remove-btn" onClick={() => setShowCategoryModal(false)} title="Cerrar">×</button>
                        </div>
                        <div style={modalStyles.body}>
                            <p>¿Qué categoría te gustaría agregar? Tu sugerencia será revisada por nuestro equipo.</p>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Escribe tu sugerencia de categoría"
                                value={categorySuggestion}
                                onChange={(e) => setCategorySuggestion(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCategorySuggestion(); } }}
                            />
                        </div>
                        <div style={modalStyles.footer}>
                            <button className="btn btn-outline" onClick={() => setShowCategoryModal(false)}>Cancelar</button>
                            <button
                                className="btn btn-primary"
                                onClick={addCategorySuggestion}
                                disabled={!categorySuggestion.trim()}
                                style={{ backgroundColor: categorySuggestion.trim() ? 'var(--primary-color)' : undefined }}
                            >
                                Agregar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal sugerencia de subcategoría */}
            {showSubcategoryModal && (
                <div style={modalStyles.overlay}>
                    <div style={modalStyles.content}>
                        <div style={modalStyles.header}>
                            <h3 style={{ margin: 0 }}>Sugerir nueva subcategoría</h3>
                            <button className="remove-btn" onClick={() => setShowSubcategoryModal(false)} title="Cerrar">×</button>
                        </div>
                        <div style={modalStyles.body}>
                            <p>
                                ¿Qué subcategoría te gustaría agregar para <strong>{categoryForSuggestion}</strong>? Tu sugerencia será revisada.
                            </p>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Escribe tu sugerencia de subcategoría"
                                value={subcategorySuggestion}
                                onChange={(e) => setSubcategorySuggestion(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addSubcategorySuggestion(); } }}
                            />
                        </div>
                        <div style={modalStyles.footer}>
                            <button className="btn btn-outline" onClick={() => setShowSubcategoryModal(false)}>Cancelar</button>
                            <button
                                className="btn btn-primary"
                                onClick={addSubcategorySuggestion}
                                disabled={!subcategorySuggestion.trim()}
                                style={{ backgroundColor: subcategorySuggestion.trim() ? 'var(--primary-color)' : undefined }}
                            >
                                Agregar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CategoriesStep;
